import React, { useEffect } from 'react';

/**
 * Modal (Dialog)
 * Props:
 *   visible / isOpen — boolean
 *   onClose  — called on close or cancel
 *   title    — string | ReactNode
 *   size     — 'sm' | 'md' | 'lg'
 *   children — modal content area (.modal__body and .modal__footer)
 */
const Modal = ({ visible, isOpen, onClose, title, size = 'md', children }) => {
  const showModal = visible !== undefined ? !!visible : (isOpen !== undefined ? !!isOpen : true);

  // Handle ESC key press
  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  if (!showModal) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className={`modal ${size === 'sm' ? 'modal--sm' : size === 'lg' ? 'modal--lg' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          {onClose && (
            <button
              type="button"
              className="modal__close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ✕
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default Modal;
